import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

/**
 * Landing page component showcasing Pixel's creator management platform
 * 
 * @component
 * @returns {React.ReactElement} The rendered Home component
 */
function Home() {
  useEffect(() => {
    // Smooth scroll for anchor links
    const handleScroll = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const links = document.querySelectorAll('a[href^="#"]');
    links.forEach(link => link.addEventListener('click', handleScroll));

    return () => {
      links.forEach(link => link.removeEventListener('click', handleScroll));
    };
  }, []);

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-logo">Pixel</div>
          <div className="hero-badge">Creator Management Platform</div>
          <h1 className="hero-title">
            Empower Your Creators.
            <br />
            <span className="gradient-text">Amplify Your Business.</span>
          </h1>
          <p className="hero-description">
            The all-in-one platform for managing creator relationships, content workflows, 
            and performance analytics. Built for agencies and creator networks.
          </p>
          <div className="hero-cta">
            <Link to="/call" className="btn btn-primary">
              Get Started
            </Link>
            <a href="#features" className="btn btn-secondary">
              Learn More
            </a>
          </div>
        </div>
        
        <div className="hero-visual">
          <div className="floating-card card-1">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h4>Analytics</h4>
              <p>Real-time insights</p>
            </div>
          </div>
          <div className="floating-card card-2">
            <div className="card-icon">🎬</div>
            <div className="card-content">
              <h4>Content</h4>
              <p>Centralized library</p>
            </div>
          </div>
          <div className="floating-card card-3">
            <div className="card-icon">🤖</div>
            <div className="card-content">
              <h4>Automation</h4>
              <p>Smart workflows</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2 className="section-title">Everything You Need</h2>
          <p className="section-subtitle">
            Powerful tools designed for modern creator management
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Lead Management</h3>
            <p>
              Track and manage creator relationships from first contact through onboarding. 
              Advanced filtering, search, and pipeline management tools.
            </p>
            <ul className="feature-list">
              <li>Creator profile tracking</li>
              <li>Status and stage management</li>
              <li>Automated onboarding workflows</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Analytics Dashboard</h3>
            <p>
              Comprehensive performance tracking across social platforms. 
              Make data-driven decisions with real-time insights and reporting.
            </p>
            <ul className="feature-list">
              <li>Performance metrics</li>
              <li>Engagement analytics</li>
              <li>Custom reporting</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🎬</div>
            <h3>Content Management</h3>
            <p>
              Centralized content library powered by CDN technology. Organize, preview, 
              and manage creator content with ease.
            </p>
            <ul className="feature-list">
              <li>Cloud-based storage</li>
              <li>Automatic organization</li>
              <li>Thumbnail generation</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>Workflow Automation</h3>
            <p>
              Automate repetitive tasks with intelligent scheduling, queue management, 
              and content posting workflows.
            </p>
            <ul className="feature-list">
              <li>Automated posting schedules</li>
              <li>Task management system</li>
              <li>Follow-up reminders</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Payment Processing</h3>
            <p>
              Manage creator payouts, revenue splits, and financial tracking 
              all in one place.
            </p>
            <ul className="feature-list">
              <li>Split assignment</li>
              <li>Payout tracking</li>
              <li>Financial reporting</li>
            </ul>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📋</div>
            <h3>Task Management</h3>
            <p>
              Keep your team organized with comprehensive task tracking, 
              assignments, and deadline management.
            </p>
            <ul className="feature-list">
              <li>Task templates</li>
              <li>Team assignments</li>
              <li>Progress tracking</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-number">10K+</div>
            <div className="stat-label">Content Files Managed</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">500+</div>
            <div className="stat-label">Active Creators</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">99.9%</div>
            <div className="stat-label">Uptime</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support Available</div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-header">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Get started in minutes with our streamlined process
          </p>
        </div>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Onboard Creators</h3>
            <p>
              Use our intuitive call forms and lead capture to bring new creators 
              into your network. Track their social profiles and engagement metrics.
            </p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step">
            <div className="step-number">2</div>
            <h3>Manage Content</h3>
            <p>
              Upload, organize, and schedule creator content. Our CDN ensures 
              fast delivery and our automation handles the posting.
            </p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step">
            <div className="step-number">3</div>
            <h3>Track Performance</h3>
            <p>
              Monitor analytics, engagement, and revenue. 
              Make informed decisions with comprehensive reporting and insights.
            </p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step">
            <div className="step-number">4</div>
            <h3>Optimize & Scale</h3>
            <p>
              Use data-driven insights to refine strategies, automate workflows, 
              and scale your creator network efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Scale Your Creator Network?</h2>
          <p>
            Join leading agencies and networks using Pixel to manage their creator relationships.
          </p>
          <div className="cta-buttons">
            <Link to="/call" className="btn btn-primary btn-large">
              Get Started Today
            </Link>
            <Link to="/support" className="btn btn-secondary btn-large">
              Contact Support
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Pixel</h4>
            <p>Creator Management Platform</p>
            <p className="footer-address">
              4515 E Pershing Blvd<br />
              Cheyenne, WY 82001<br />
              United States
            </p>
          </div>
          
          <div className="footer-section">
            <h4>Platform</h4>
            <ul>
              <li><a href="#features">Features</a></li>
              <li><Link to="/call">Get Started</Link></li>
              <li><Link to="/support">Support</Link></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><a href="#terms">Terms of Service</a></li>
              <li><a href="#refund-policy">Refund Policy</a></li>
            </ul>
            <p className="footer-note">Governing Jurisdiction: Wyoming, USA</p>
          </div>
          
          <div className="footer-section">
            <h4>Customer Service</h4>
            <ul>
              <li><a href="mailto:julie@pixeltalent.uk">julie@pixeltalent.uk</a></li>
              <li><Link to="/support">Contact Support</Link></li>
            </ul>
            <p className="footer-note">Refund/Cancellation Policy: No refunds or returns</p>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Pixel. All rights reserved. | Registered Business Address: 4515 E Pershing Blvd, Cheyenne, WY 82001</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;

